import * as React from 'react';
import { styled } from '@glitz/react';
import Image from 'webapp/components/image';
import { Block, edgeMarginsDecorator, maxContentNormalWidthDecorator } from 'webapp/components/page-layout';
import {
  createMaxSmallViewportDecorator,
  createMinLargeViewportDecorator,
  createMinMediumViewportDecorator,
} from 'webapp/viewport';
import { useMatchMobile } from '@avensia/excite';
import { isSvenssons } from 'webapp/theme';
import { ContentKeys, isContentCleaned } from 'webapp/utils/content-cleaner';

type PropType = {
  heading?: string;
  topSmallViewportImage?: Excite.ContentApi.ImageApiModel;
  topLargeViewportImage?: Excite.ContentApi.ImageApiModel;
  shortDescription?: string;
  opacity?: number;
  showCmsContent: boolean;
};

export function ProductListingTopImage(props: PropType) {
  const isMobile = useMatchMobile();

  const image = isMobile ? props.topSmallViewportImage || props.topLargeViewportImage : props.topLargeViewportImage;
  const showImage = Boolean(image) && props.showCmsContent && !isContentCleaned(ContentKeys.TopImage);

  const content = props.heading && (
    <Content css={contentDecorator(showImage)}>
      <Heading>{props.heading}</Heading>
      {props.shortDescription && props.showCmsContent && <Description>{props.shortDescription}</Description>}
    </Content>
  );

  const wrapperDecorator = React.useMemo(() => {
    const opacity = props.opacity ? props.opacity : isSvenssons ? 0.2 : 0.5;
    return isSvenssons
      ? styled({
          backgroundImage: `linear-gradient(90deg, transparent 0%, rgba(0, 0, 0, 0.2) 50%, transparent 100%)`,
          backgroundColor: 'transparent',
        })
      : styled(
          createMinMediumViewportDecorator({
            backgroundImage: `linear-gradient(90deg,rgba(0,0,0, ${opacity}) 0,transparent 700px,transparent)`,
            backgroundColor: 'transparent',
          }),
          createMinLargeViewportDecorator({
            backgroundImage: `linear-gradient(90deg,rgba(0,0,0, ${opacity}) 0,transparent 65%,transparent)`,
            backgroundColor: 'transparent',
          }),
          {
            backgroundColor: `rgba(0,0,0,${opacity})`,
          },
        );
  }, [props.opacity]);

  return props.heading ? (
    <Base>
      {showImage ? (
        <>
          <ImageTop image={image} />
          <ContentWrapper css={wrapperDecorator}>{content}</ContentWrapper>
        </>
      ) : (
        content
      )}
    </Base>
  ) : null;
}

const Base = styled.div(
  {
    position: 'relative',
  },
  isSvenssons && {
    display: 'flex',
    justifyContent: 'center',
    textAlign: 'center',
    marginBottom: t => t.sharedSpacing.m,
  },
);

const Heading = styled.h1(
  createMaxSmallViewportDecorator({
    fontSize: t => t.sharedFontSize.m,
    marginBottom: t => t.sharedSpacing.m,
  }),
  {
    fontSize: t => t.fontSize.xl,
    marginBottom: t => (isSvenssons ? t.sharedSpacing.xl : t.sharedSpacing.l),
  },
);

const ImageTop = styled(Image, {
  width: '100%',
});

const Description = styled.div(
  createMaxSmallViewportDecorator({
    fontSize: t => t.fontSize.xxs,
  }),
  {
    maxWidth: 600,
    fontSize: t => t.fontSize.xs,
  },
  isSvenssons && {
    margin: { x: 'auto' },
  },
);

function contentDecorator(hasImage: boolean) {
  return hasImage
    ? styled({
        position: 'absolute',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        color: 'white',
      })
    : styled(createMinLargeViewportDecorator({ padding: { top: t => t.spacing.xl } }), {
        padding: { top: t => t.spacing.m },
      });
}

const ContentWrapper = styled.div({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
});

const Content = styled(
  Block,
  edgeMarginsDecorator,
  maxContentNormalWidthDecorator,
  isSvenssons &&
    createMaxSmallViewportDecorator({
      padding: { x: t => t.sharedSpacing.xl },
    }),
);
